import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { hashForRoute } from "../lib/hashRoute.js";
import type { DashboardSummary } from "../lib/types.js";

const ONLINE_WINDOW_MS = 60_000;
/** Ambient, not a wait: refresh slower than a job-detail page. */
const DASHBOARD_POLL_MS = 10_000;

const card =
  "rounded-md border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900";

/** A count/amount from the API can arrive `null` (a NaN counter serialized by
 *  the server) - render it as 0 rather than throwing inside `.toFixed`. */
function n(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

function Stat({ label, value, href }: { label: string; value: number | string; href?: string }) {
  const inner = (
    <>
      <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{label}</div>
    </>
  );
  return href ? (
    <a href={href} className={`${card} block hover:border-(--accent)`}>
      {inner}
    </a>
  ) : (
    <div className={card}>{inner}</div>
  );
}

export function Dashboard() {
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api<DashboardSummary>("/api/dashboard");
      setSummary(res);
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setError(err instanceof Error ? err.message : "Could not load the dashboard.");
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), DASHBOARD_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const now = Date.now();

  if (error) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dashboard</h1>
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  const { deliverables, jobs, spend, routes, workers, setup } = summary;
  const incompleteSetup = setup.filter((s) => !s.done);

  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Dashboard</h1>

      {incompleteSetup.length > 0 && (
        <section className="mt-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Getting set up ({setup.length - incompleteSetup.length} of {setup.length})
          </h2>
          <ul className="mt-2 space-y-1">
            {setup.map((s) => (
              <li
                key={s.id}
                className={`flex items-start gap-2 rounded border px-3 py-2 text-sm ${
                  s.done
                    ? "border-gray-200 text-gray-400 dark:border-gray-800"
                    : "border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950"
                }`}
              >
                <span aria-hidden className={s.done ? "text-green-600" : "text-amber-600"}>
                  {s.done ? "✔" : "○"}
                </span>
                <span>
                  <span
                    className={
                      s.done
                        ? "line-through"
                        : "font-medium text-gray-900 dark:text-gray-100"
                    }
                  >
                    {s.label}
                  </span>
                  {!s.done && (
                    <span className="block text-xs text-gray-600 dark:text-gray-400">{s.hint}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Needs you</h2>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="in review" value={deliverables.review} href={hashForRoute({ kind: "review" })} />
          <Stat label="pending" value={deliverables.pending} />
          <Stat label="generating" value={deliverables.generating} />
          <Stat label="accepted" value={deliverables.accepted} />
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">In flight</h2>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat
            label="queued"
            value={jobs.queued}
            href={hashForRoute({ kind: "jobs", status: "queued" })}
          />
          <Stat
            label="running"
            value={jobs.running}
            href={hashForRoute({ kind: "jobs", status: "running" })}
          />
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Last 24 hours</h2>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="succeeded" value={jobs.succeededLast24h} href={hashForRoute({ kind: "jobs" })} />
          <Stat
            label="failed"
            value={jobs.failedLast24h}
            href={hashForRoute({ kind: "jobs", status: "failed" })}
          />
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Spend this month ({spend.month})
        </h2>
        <div className={`${card} mt-2`}>
          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            ${n(spend.totalUsd).toFixed(2)}
          </div>
          {spend.byRoute.length === 0 ? (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Nothing billed yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-gray-100 text-sm dark:divide-gray-800">
              {spend.byRoute.map((r) => (
                <li key={`${r.routeId}#${r.contentType}`} className="flex justify-between py-1.5">
                  <span className="text-gray-600 dark:text-gray-300">
                    {r.routeId} <span className="text-gray-400">/ {r.contentType}</span>
                  </span>
                  <span className="tabular-nums text-gray-900 dark:text-gray-100">
                    ${n(r.usd).toFixed(4)} <span className="text-gray-400">({n(r.jobs)})</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Routes</h2>
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat
            label="enabled / total"
            value={`${routes.enabled} / ${routes.total}`}
            href={hashForRoute({ kind: "routes" })}
          />
          <Stat
            label="need verification"
            value={routes.needsVerification}
            href={hashForRoute({ kind: "routes" })}
          />
        </div>
      </section>

      <section className="mt-6">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Local workers</h2>
        {workers.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            None registered. Mint a <code>worker</code>-scope token and run{" "}
            <code>npm start --workspace worker</code> on the PC.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {workers.map((w) => {
              const online = now - Date.parse(w.lastSeenAt) < ONLINE_WINDOW_MS;
              return (
                <li
                  key={w.workerId}
                  className="flex items-center justify-between rounded border border-gray-200 px-3 py-2 text-sm dark:border-gray-700"
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${online ? "bg-green-500" : "bg-gray-400"}`}
                      title={online ? "online" : "offline"}
                    />
                    <span className="font-medium text-gray-900 dark:text-gray-100">{w.workerId}</span>
                    <span className="text-gray-500 dark:text-gray-400">{w.hostname}</span>
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {w.currentJobId ? `job ${w.currentJobId.slice(0, 8)}` : `${w.routeIds.length} routes`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
